"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  ColorSwatches,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import { OrgChartDiagram } from "@/components/tools/diagrams-more/org-chart-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { type ChartColorId, colorIndex } from "@/lib/tool-kit/charts/color";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { diagramColor } from "@/lib/tool-kit/diagrams/color";
import {
  branchOf,
  layoutOrgChart,
  ORG_LIMIT,
  orgCounts,
  orgOutline,
  orgRows,
  parseOrgChart,
} from "@/lib/tool-kit/diagrams-more/org-chart";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const ORG_CHART_MAKER_STRINGS = {
  inputLabel:
    "Your organization — one person or unit per line, indented under who they report to",
  inputHint:
    "Write Name | Title. Add more | parts for a department or a second line. A line indented under another reports to it; lines with no one above them are separate tops, drawn side by side. Indent with spaces or tabs; bullets and numbers are removed.",
  exampleText:
    "Maria Okafor | Chief Executive\n  Daniel Reyes | Operations Director\n    Priya Nair | Warehouse Manager\n    Tom Becker | Fleet Manager\n    Aisha Khan | Customer Service Lead\n  Lena Fischer | Finance Director\n    Omar Haddad | Accounts Payable\n    Julia Moreau | Payroll\n  Samuel Ito | People Director\n    Chloe Park | Recruitment\n    Ben Walker | Learning and Development\n  Hannah Cole | Technology Director\n    Ravi Patel | Software Lead\n    Elena Rossi | IT Support",
  exampleTitle: "Northfield Logistics: Leadership Team",
  loadExample: "Load the example",
  clear: "Clear",
  compact: "Stack single-box teams in a hanging column",
  compactHint:
    "A unit whose reports have no one under them lists them down the page instead of across it, which keeps wide teams narrow.",
  colorBranches: "Give each first-level unit its own color",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  firstColor: "Color of the first unit (the rest follow in order)",
  oneColor: "Chart color",
  peopleDetected: "one:{count} box|other:{count} boxes",
  levelsDetected: "one:{count} level|other:{count} levels",
  enterList:
    "Type at least one line, such as a name and title, to start the chart.",
  dropped:
    "one:{count} line was left out — a chart holds up to {limit} boxes.|other:{count} lines were left out — a chart holds up to {limit} boxes.",
  levelColumn: "Level",
  nameColumn: "Name",
  titleColumn: "Title",
  reportsToColumn: "Reports to",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title (optional)",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy as indented list",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the chart as a table",
  chartLabel: "Organizational chart",
  chartDescription:
    "Organizational chart with {boxes} over {levels}, starting from {top}.",
} as const;

export default function OrgChartMakerWidget() {
  const strings = useToolUi(ORG_CHART_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleText);
  const [compact, setCompact] = useState(true);
  const [byBranch, setByBranch] = useState(true);
  const [colorId, setColorId] = useState<ChartColorId>("blue");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parse = parseOrgChart(raw);
  const layout = parse.nodes.length > 0 ? layoutOrgChart(parse, compact) : null;
  const counts = orgCounts(parse);
  const start = colorIndex(colorId);
  const colorOf = (id: number): string => {
    if (!byBranch) {
      return CHART_COLORS[start];
    }
    const branch = branchOf(parse, id);
    return branch < 0 ? CHART_INK.text : diagramColor(start, branch);
  };
  const boxesText = tPlural(strings.peopleDetected, locale, counts.people, {
    count: fmt(counts.people),
  });
  const levelsText = tPlural(strings.levelsDetected, locale, counts.levels, {
    count: fmt(counts.levels),
  });
  const rows = orgRows(parse).map((row) => [
    fmt(row.level),
    row.name,
    row.details.join(" · "),
    row.reportsTo,
  ]);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="ocm-input"
        label={strings.inputLabel}
        onChange={setRaw}
        rows={14}
        value={raw}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw(strings.exampleText)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div aria-live="polite" className="space-y-2">
        {layout ? (
          <p className={HINT}>
            {boxesText}
            {" · "}
            {levelsText}
          </p>
        ) : (
          <Notice>{strings.enterList}</Notice>
        )}
        {parse.dropped > 0 ? (
          <Notice>
            {tPlural(strings.dropped, locale, parse.dropped, {
              count: fmt(parse.dropped),
              limit: fmt(ORG_LIMIT),
            })}
          </Notice>
        ) : null}
      </div>
      <div className="space-y-2">
        <Checkbox
          checked={compact}
          label={strings.compact}
          onChange={setCompact}
        />
        <p className={HINT}>{strings.compactHint}</p>
      </div>
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={orgOutline(parse)}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "org-chart")}
          labels={strings}
          note={style.note}
          table={{
            head: [
              strings.levelColumn,
              strings.nameColumn,
              strings.titleColumn,
              strings.reportsToColumn,
            ],
            rows,
          }}
          title={style.title}
        >
          <OrgChartDiagram
            colorOf={colorOf}
            description={t(strings.chartDescription, {
              boxes: boxesText,
              levels: levelsText,
              top: parse.nodes[parse.roots[0]].name,
            })}
            id="ocm-chart"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="ocm"
        labels={strings}
        style={style}
        update={update}
      >
        <Checkbox
          checked={byBranch}
          label={strings.colorBranches}
          onChange={setByBranch}
        />
        <ColorSwatches
          labels={strings}
          legend={byBranch ? strings.firstColor : strings.oneColor}
          name="ocm-color"
          onChange={setColorId}
          value={colorId}
        />
      </ChartStyleFields>
    </div>
  );
}
